import React, { useEffect, useMemo } from 'react';
import Navbar from '@/components/Navbar';
import Section from '@/components/Section';
import Footer from '@/components/Footer';
import CountdownTimer from '@/components/CountdownTimer';
import RSVPForm from '@/components/RSVPForm';
import { Clock, MapPin, ExternalLink } from 'lucide-react';

const Index: React.FC = () => {
  // Wedding date: February 14th, 2026
  const weddingDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(2026);
    date.setMonth(1); // February (0-based)
    date.setDate(14);
    date.setHours(13);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }, []);

  // Images for each section
  const welcomeImage = '/welcomeimage.jpg';
  const prebodaImage = '/bar.jpg';
  const ceremoniaImage = '/church-bg.jpeg';
  const celebracionImage = '/intercontinental1.jpg';

  // State for the scroll gradient overlay opacity
  const [scrollOverlayOpacity, setScrollOverlayOpacity] = React.useState(0);

  useEffect(() => {
    document.title = "Victoria & Sanro | 14 de febrero de 2026";

    // Scroll event listener for the gradient overlay
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Calculate opacity based on scroll position
      const newOpacity = Math.min(scrollY / 400, 0.6); // Adjusted max opacity
      setScrollOverlayOpacity(newOpacity);
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  return (
    <div className="min-h-screen bg-wedding-bone">
      {/* Scroll Gradient Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '18vh', // Slightly reduced height
          backgroundImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.9), transparent)', // Slightly darker gradient start
          opacity: scrollOverlayOpacity,
          pointerEvents: 'none',
          zIndex: 40,
          transition: 'opacity 0.4s ease-out', // Adjusted transition
        }}
      ></div>

      <Navbar />

      {/* Welcome Section */}
      <Section 
        id="inicio" 
        title="" 
        backgroundImage={welcomeImage}
        overlayStyle="darker"
        className="pt-0 flex items-center justify-center"
        applyBlur={true}
      >
        <div className="flex flex-col items-center justify-center min-h-screen text-center">
          <div className="reveal hero-text relative">
            <div className="relative px-8 py-12">
              <h1 className="text-5xl md:text-7xl font-playfair mb-3 text-white drop-shadow-lg font-bold animate-fade-in">
                Victoria & Sanro
              </h1>
              <p className="text-xl md:text-2xl mb-12 font-light text-white drop-shadow-md animate-fade-in-delay">
                14 de febrero de 2026 · Madrid
              </p>
            </div>
          </div>
          
          <div className="my-12 reveal">
            <CountdownTimer targetDate={weddingDate} />
          </div>
          
          <div className="max-w-xl mx-auto reveal glass-card p-8 rounded-2xl shadow-2xl font-outfit backdrop-blur-xl bg-white/10 border border-white/20">
            <p className="text-lg md:text-xl mb-4 text-white font-playfair">¡Bienvenidos!</p>
            <p className="text-lg md:text-xl mb-4 text-white/90">
              Nos hace mucha ilusión compartir con vosotros un momento tan especial en nuestras vidas.
            </p>
            <p className="text-lg md:text-xl mb-4 text-white/90">
              Aquí encontraréis toda la información de interés para acompañarnos en nuestra boda.
            </p>
          </div>
        </div>
      </Section>

      {/* Preboda Section */}
      <Section 
        id="preboda" 
        title="Preboda (Tentativa)"
        backgroundImage={prebodaImage}
        overlayStyle="darker"
        className="section-image"
        applyBlur={true}
      >
        <div className="container relative z-10">
          <div className="min-h-screen flex flex-col justify-center py-20">
            <div className="flex flex-col items-center justify-center h-full text-center max-w-2xl mx-auto">
              <h3 className="text-3xl md:text-4xl font-playfair mb-6 font-bold text-white drop-shadow-lg">
                Preboda — Evento Tentativo
              </h3>
              <p className="text-lg md:text-xl mb-8 font-outfit text-white/90">
                Estamos valorando la posibilidad de organizar una preboda la noche anterior a la boda (13 de febrero), para poder compartir un momento más relajado y distendido con todos vosotros. Todavía no está confirmado el lugar ni los detalles, pero nos encantaría saber cuántos estaríais interesados en asistir para poder organizarlo mejor.
              </p>
              <p className="text-lg md:text-xl mb-8 font-outfit text-white/90">
                Si te gustaría venir a la preboda en caso de que finalmente se celebre, por favor indícalo en el formulario de RSVP. Así podremos hacer una estimación y preparar algo especial si sale adelante.
              </p>
              <div className="glass-card p-6 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20 shadow-lg mt-6">
                <p className="text-base md:text-lg text-white/80 font-outfit">
                  ¡Gracias por tu interés y por ayudarnos a organizar un fin de semana inolvidable!
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Ceremonia Section */}
      <Section 
        id="ceremonia" 
        title="Ceremonia"
        backgroundImage={ceremoniaImage}
        overlayStyle="darker"
        className="section-image"
        applyBlur={true}
      >
        <div className="container relative z-10">
          <div className="min-h-screen flex flex-col justify-center py-20">
            {/* Mobile Layout */}
            <div className="md:hidden">
              <div className="grid grid-cols-1 gap-8">
                {/* Main Ceremony Content */}
                <div className="relative z-20 p-4 md:p-8 text-white drop-shadow-lg">
                  <h3 className="text-2xl md:text-3xl font-playfair mb-4 font-bold">
                    Iglesia de Los Jerónimos del Real
                  </h3>
                  <p className="text-base md:text-lg mb-6 font-outfit">
                  La ceremonia se celebrará en uno de los rincones más emblemáticos y con encanto de Madrid, un lugar cargado de historia y belleza que hará de este momento algo aún más especial.
                  </p>
                    
                  <div className="space-y-4 mb-6 font-outfit">
                    <a 
                      href="https://maps.app.goo.gl/jazK2vYRkg5zrDEG9"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 hover:underline font-medium text-white"
                    >
                      <MapPin className="w-6 h-6 text-white" />
                      <span className="text-lg">
                        Iglesia de Los Jerónimos del Real
                        <ExternalLink className="inline-block w-5 h-5 ml-2 text-white" />
                      </span>
                    </a>
                    <div className="flex items-center space-x-3 text-white">
                      <Clock className="w-5 h-5 text-white" />
                      <span className="text-base md:text-lg">13:00 PM</span>
                    </div>
                  </div>
                </div>

                {/* Additional Information Blocks */}
                <div className="space-y-6">
                  <div className="relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl">
                    <h4 className="text-xl md:text-2xl font-playfair mb-4 text-white font-semibold">
                      El Lugar
                    </h4>
                    <p className="text-base md:text-lg mb-4 text-white font-outfit">
                    La ceremonia tendrá lugar en la Iglesia de los Jerónimos, un espacio emblemático de Madrid con siglos de historia. Su arquitectura y su entorno aportan un carácter único a este día, en un lugar que forma parte del patrimonio de la ciudad.
                    </p>
                  </div>

                  <div className="relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl">
                    <h4 className="text-xl md:text-2xl font-playfair mb-4 text-white font-semibold">
                      Información Importante
                    </h4>
                    <ul className="space-y-3 text-base md:text-lg text-white font-outfit">
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        La ceremonia comenzará a las 13:00
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Se recomienda llegar con antelación para evitar retrasos
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        El aparcamiento en la zona es limitado; Parking Las Cortes (Telpark by Empark) se encuentra a 5 minutos a pie
                      </li>
                    </ul>
                  </div>


                  <div className="relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl">
                    <h4 className="text-xl md:text-2xl font-playfair mb-4 text-white font-semibold">
                      Detalles de la Ceremonia
                    </h4>
                    <ul className="space-y-3 text-base md:text-lg text-white font-outfit">
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Ceremonia católica según el rito ordinario
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Dress code: Elegante
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:block">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                  <h3 className="text-4xl font-playfair mb-6 font-bold text-white drop-shadow-lg">
                    Iglesia de Los Jerónimos del Real
                  </h3>
                  <p className="text-xl mb-8 font-outfit text-white/90 max-w-2xl mx-auto">
                    La ceremonia se celebrará en uno de los rincones más emblemáticos y con encanto de Madrid, un lugar cargado de historia y belleza que hará de este momento algo aún más especial.
                  </p>
                  
                  <div className="flex justify-center space-x-8 mb-12 font-outfit">
                    <a 
                      href="https://maps.app.goo.gl/jazK2vYRkg5zrDEG9"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 hover:underline font-medium text-white"
                    >
                      <MapPin className="w-6 h-6 text-white" />
                      <span className="text-lg">
                        Iglesia de Los Jerónimos del Real
                        <ExternalLink className="inline-block w-5 h-5 ml-2 text-white" />
                      </span>
                    </a>
                    <div className="flex items-center space-x-3 text-white">
                      <Clock className="w-6 h-6 text-white" />
                      <span className="text-lg">13:00 PM</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-8">
                  <div className="glass-card p-8 rounded-2xl backdrop-blur-xl bg-black/20 border border-white/10">
                    <h4 className="text-2xl font-playfair mb-4 text-white font-semibold text-center">
                      El Lugar
                    </h4>
                    <p className="text-base text-white/90 font-outfit text-center">
                      La ceremonia tendrá lugar en la Iglesia de los Jerónimos, un espacio emblemático de Madrid con siglos de historia. Su arquitectura y su entorno aportan un carácter único a este día, en un lugar que forma parte del patrimonio de la ciudad.
                    </p>
                  </div>

                  <div className="glass-card p-8 rounded-2xl backdrop-blur-xl bg-black/20 border border-white/10">
                    <h4 className="text-2xl font-playfair mb-4 text-white font-semibold text-center">
                      Información Importante
                    </h4>
                    <ul className="space-y-3 text-base text-white/90 font-outfit">
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        La ceremonia comenzará a las 13:00
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Se recomienda llegar con antelación para evitar retrasos
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        El aparcamiento en la zona es limitado; Parking Las Cortes (Telpark by Empark) se encuentra a 5 minutos a pie
                      </li>
                    </ul>
                  </div>

                  <div className="glass-card p-8 rounded-2xl backdrop-blur-xl bg-black/20 border border-white/10">
                    <h4 className="text-2xl font-playfair mb-4 text-white font-semibold text-center">
                      Detalles de la Ceremonia
                    </h4>
                    <ul className="space-y-3 text-base text-white/90 font-outfit">
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Ceremonia católica según el rito ordinario
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Dress code: Elegante
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Celebración Section */}
      <Section
        id="celebracion"
        title="Celebración"
        backgroundImage={celebracionImage}
        overlayStyle="darker"
        className="section-image"
        applyBlur={true}
      >
        <div className="container relative z-10">
          <div className="min-h-screen flex flex-col justify-center py-20">
            {/* Mobile Layout */}
            <div className="md:hidden">
              <div className="grid grid-cols-1 gap-8">
                {/* Main Celebration Content */}
                <div className="relative z-20 p-4 md:p-8 text-white drop-shadow-lg">
                  <h3 className="text-2xl md:text-3xl font-playfair mb-4 font-bold">
                    Hotel InterContinental Madrid
                  </h3>
                  <p className="text-base md:text-lg mb-6 font-outfit">
                  Tras la ceremonia, la celebración continuará en el Hotel InterContinental Madrid, un referente de elegancia en pleno Paseo de la Castellana y a pocos minutos de la iglesia. Hemos elegido este espacio por su ambiente sofisticado y su cuidada atención al detalle, ideal para disfrutar de una celebración festiva y elegante.
                  </p>
                    
                  <div className="space-y-4 mb-6 font-outfit">
                    <a 
                      href="https://maps.app.goo.gl/pvrDnw5unumb2vNTA"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 hover:underline font-medium text-white"
                    >
                      <MapPin className="w-5 h-5 text-white" />
                      <span className="text-base md:text-lg">
                        Hotel Intercontinental
                        <ExternalLink className="inline-block w-4 h-4 ml-2 text-white" />
                      </span>
                    </a>
                    <div className="flex items-center space-x-3 text-white">
                      <Clock className="w-5 h-5 text-white" />
                      <span className="text-base md:text-lg">14:30 PM</span>
                    </div>
                  </div>
                </div>

                {/* Additional Information Blocks */}
                <div className="space-y-6">
                  <div className="relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl">
                    <h4 className="text-xl md:text-2xl font-playfair mb-4 text-white font-semibold">
                      El Lugar
                    </h4>
                    <p className="text-base md:text-lg mb-4 text-white font-outfit">
                    El Hotel InterContinental Madrid es un referente de la ciudad, conocido por su historia y su carácter atemporal. Sus salones, cuidadosamente diseñados, ofrecen un entorno elegante y acogedor para la celebración, en una ubicación privilegiada del centro de Madrid.
                    </p>
                  </div>

                  <div className="relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl">
                    <h4 className="text-xl md:text-2xl font-playfair mb-4 text-white font-semibold">
                      Información Importante
                    </h4>
                    <ul className="space-y-3 text-base md:text-lg text-white font-outfit">
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        La recepción comenzará sobre las 14:30&nbsp;— se ruega puntualidad
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        El aparcamiento en la zona es limitado y no se puede utilizar el parking del hotel
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Se recomienda utilizar taxi o VTC desde la iglesia
                      </li>
                    </ul>
                  </div>

                  <div className="relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl">
                    <h4 className="text-xl md:text-2xl font-playfair mb-4 text-white font-semibold">
                      Detalles de la Celebración
                    </h4>
                    <ul className="space-y-3 text-base md:text-lg text-white font-outfit">
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Recepción con aperitivos y cóctel
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Comida de gala
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Fiesta posterior
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:block">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                  <h3 className="text-4xl font-playfair mb-6 font-bold text-white drop-shadow-lg">
                    Hotel InterContinental Madrid
                  </h3>
                  <p className="text-xl mb-8 font-outfit text-white/90 max-w-2xl mx-auto">
                    Tras la ceremonia, la celebración continuará en el Hotel InterContinental Madrid, un referente de elegancia en pleno Paseo de la Castellana y a pocos minutos de la iglesia. Hemos elegido este espacio por su ambiente sofisticado y su cuidada atención al detalle, ideal para disfrutar de una celebración festiva y elegante.
                  </p>
                  
                  <div className="flex justify-center space-x-8 mb-12 font-outfit">
                    <a 
                      href="https://maps.app.goo.gl/pvrDnw5unumb2vNTA"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 hover:underline font-medium text-white"
                    >
                      <MapPin className="w-6 h-6 text-white" />
                      <span className="text-lg">
                        Hotel Intercontinental
                        <ExternalLink className="inline-block w-5 h-5 ml-2 text-white" />
                      </span>
                    </a>
                    <div className="flex items-center space-x-3 text-white">
                      <Clock className="w-6 h-6 text-white" />
                      <span className="text-lg">14:30 PM</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-8">
                  <div className="glass-card p-8 rounded-2xl backdrop-blur-xl bg-black/20 border border-white/10">
                    <h4 className="text-2xl font-playfair mb-4 text-white font-semibold text-center">
                      El Lugar
                    </h4>
                    <p className="text-base text-white/90 font-outfit text-center">
                      El Hotel InterContinental Madrid es un referente de la ciudad, conocido por su historia y su carácter atemporal. Sus salones, cuidadosamente diseñados, ofrecen un entorno elegante y acogedor para la celebración, en una ubicación privilegiada del centro de Madrid.
                    </p>
                  </div>

                  <div className="glass-card p-8 rounded-2xl backdrop-blur-xl bg-black/20 border border-white/10">
                    <h4 className="text-2xl font-playfair mb-4 text-white font-semibold text-center">
                      Información Importante
                    </h4>
                    <ul className="space-y-3 text-base text-white/90 font-outfit">
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        La recepción comenzará sobre las 14:30 — se ruega puntualidad
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        El aparcamiento en la zona es limitado y no se puede utilizar el parking del hotel
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Se recomienda utilizar taxi o VTC desde la iglesia
                      </li>
                    </ul>
                  </div>

                  <div className="glass-card p-8 rounded-2xl backdrop-blur-xl bg-black/20 border border-white/10">
                    <h4 className="text-2xl font-playfair mb-4 text-white font-semibold text-center">
                      Detalles de la Celebración
                    </h4>
                    <ul className="space-y-3 text-base text-white/90 font-outfit">
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Recepción con aperitivos y cóctel
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Comida de gala
                      </li>
                      <li className="flex items-start">
                        <span className="text-white mr-2">•</span>
                        Fiesta posterior
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Recomendaciones Section */}
      <Section 
        id="recomendaciones" 
        title="Recomendaciones"
        backgroundImage={'/madrid.jpg'}
        overlayStyle="darker"
        className="section-image"
        applyBlur={true}
      >
        <div className="container relative z-10">
          <div className="min-h-screen flex flex-col justify-center py-20">
            <div className="md:hidden">
              <div className="grid grid-cols-1 gap-8">
                <div className="relative z-20 p-4 md:p-8 text-white drop-shadow-lg">
                  <h3 className="text-2xl md:text-3xl font-playfair mb-4 font-bold">
                    Información para Invitados
                  </h3>
                  <p className="text-base md:text-lg mb-6 font-outfit">
                    Aquí encontrarás información útil para tu estancia y desplazamientos en Madrid durante la boda.
                  </p>
                </div>
                <div className="space-y-8">
                  <div className="relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl">
                    <h4 className="text-xl md:text-2xl font-playfair mb-4 text-white font-semibold">
                      Alojamiento
                    </h4>
                    <p className="text-base md:text-lg mb-4 text-white font-outfit">
                      Hemos conseguido tarifas especiales en el Hotel InterContinental Madrid para la noche del 14 al 15 de febrero de 2026:
                    </p>
                    <ul className="space-y-2 text-base md:text-lg text-white font-outfit">
                      <li>Habitación Clásica: 260 € (solo alojamiento)</li>
                      <li>Habitación Clásica: 290 € (con desayuno)</li>
                      <li>Habitación Premium Club: 350 € (con desayuno y acceso al Club Lounge)</li>
                      <li>Precios por habitación y noche (+10% IVA). Mismas tarifas para uso doble o individual.</li>
                    </ul>
                    <p className="text-base md:text-lg mt-4 text-white font-outfit">
                      Para reservar, puedes llamar al <a href="tel:917007301" className="underline">91 700 73 01</a> o escribir a <a href="mailto:res.icmadrid@ihg.com" className="underline">res.icmadrid@ihg.com</a> indicando que asistes a la boda de Victoria y Miguel.
                    </p>
                    <p className="text-base md:text-lg mt-4 text-white/80 font-outfit">
                      Las habitaciones Premium Club incluyen acceso al Club Lounge: desayuno buffet, bebidas y canapés durante el día, hora del té, bebidas alcohólicas por la tarde, internet y otros servicios exclusivos.
                    </p>
                  </div>
                  <div className="relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl">
                    <h4 className="text-xl md:text-2xl font-playfair mb-4 text-white font-semibold">
                      Transporte
                    </h4>
                    <ul className="space-y-3 text-base md:text-lg text-white font-outfit">
                      <li>Recomendamos utilizar taxi o VTC para los traslados, ya que los lugares de la boda están muy próximos entre sí y el aparcamiento en la calle es limitado.</li>
                      <li>Hay parkings de pago cercanos tanto a la iglesia como al hotel, por si prefieres ir en coche.</li>
                    </ul>
                  </div>
                  <div className="relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl">
                    <h4 className="text-xl md:text-2xl font-playfair mb-4 text-white font-semibold">
                      Información Adicional
                    </h4>
                    <ul className="space-y-3 text-base md:text-lg text-white font-outfit">
                      <li>Confirma tu asistencia antes del 1 de enero.</li>
                      <li>Menú especial para niños disponible.</li>
                      <li>Por favor, informa de alergias o restricciones alimentarias en el formulario de RSVP.</li>
                      <li>Si tienes cualquier duda, no dudes en contactarnos.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                  <h3 className="text-4xl font-playfair mb-6 font-bold text-white drop-shadow-lg">
                    Información para Invitados
                  </h3>
                  <p className="text-xl mb-8 font-outfit text-white/90 max-w-2xl mx-auto">
                    Aquí encontrarás información útil para tu estancia y desplazamientos en Madrid durante la boda.
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-8">
                  <div className="glass-card p-8 rounded-2xl backdrop-blur-xl bg-black/20 border border-white/10">
                    <h4 className="text-2xl font-playfair mb-4 text-white font-semibold text-center">
                      Alojamiento
                    </h4>
                    <p className="text-base text-white/90 font-outfit text-center mb-4">
                      Hemos conseguido tarifas especiales en el Hotel InterContinental Madrid para la noche del 14 al 15 de febrero de 2026:
                    </p>
                    <ul className="space-y-2 text-base text-white/90 font-outfit text-left">
                      <li>Habitación Clásica: 260 € (solo alojamiento)</li>
                      <li>Habitación Clásica: 290 € (con desayuno)</li>
                      <li>Habitación Premium Club: 350 € (con desayuno y acceso al Club Lounge)</li>
                      <li>Precios por habitación y noche (+10% IVA). Mismas tarifas para uso doble o individual.</li>
                    </ul>
                    <p className="text-base mt-4 text-white/90 font-outfit text-center">
                      Para reservar, puedes llamar al <a href="tel:917007301" className="underline">91 700 73 01</a> o escribir a <a href="mailto:res.icmadrid@ihg.com" className="underline">res.icmadrid@ihg.com</a> indicando que asistes a la boda de Victoria y Miguel.
                    </p>
                    <p className="text-sm mt-2 text-white/60 font-outfit text-center">
                      Cancelación gratuita hasta las 16:00h del día anterior a la llegada. Se requiere tarjeta de crédito como garantía.
                    </p>
                  </div>
                  <div className="glass-card p-8 rounded-2xl backdrop-blur-xl bg-black/20 border border-white/10">
                    <h4 className="text-2xl font-playfair mb-4 text-white font-semibold text-center">
                      Transporte
                    </h4>
                    <ul className="space-y-3 text-base text-white/90 font-outfit text-left">
                      <li>Recomendamos utilizar taxi o VTC para los traslados, ya que los lugares de la boda están muy próximos entre sí y el aparcamiento en la calle es limitado.</li>
                      <li>Hay parkings de pago cercanos tanto a la iglesia como al hotel, por si prefieres ir en coche.</li>
                    </ul>
                  </div>
                  <div className="glass-card p-8 rounded-2xl backdrop-blur-xl bg-black/20 border border-white/10">
                    <h4 className="text-2xl font-playfair mb-4 text-white font-semibold text-center">
                      Información Adicional
                    </h4>
                    <ul className="space-y-3 text-base text-white/90 font-outfit text-left">
                      <li>Confirma tu asistencia antes del 1 de enero.</li>
                      <li>Menú especial para niños disponible.</li>
                      <li>Por favor, informa de alergias o restricciones alimentarias en el formulario de RSVP.</li>
                      <li>Si tienes cualquier duda, no dudes en contactarnos.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Regalos Section */}
      <Section 
        id="regalos" 
        title="Regalitos"
        backgroundImage={'/australia.jpg'}
        overlayStyle="darker"
        applyBlur={true}
      >
        <div className="max-w-2xl mx-auto relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl font-outfit">
          <div className="text-center py-12">
            <h2 className="text-3xl md:text-4xl font-playfair mb-6 text-white drop-shadow-lg">
              El mejor regalo que nos puedes hacer es acompañarnos en el día de nuestra boda.
            </h2>
            <p className="text-lg md:text-xl text-white drop-shadow-md mb-8">
              Si además queréis hacernos un pequeño obsequio, hemos pensado que, un aporte a nuestra nueva aventura juntos sería ideal.
            </p>
            <p className="text-lg md:text-xl text-white drop-shadow-md mb-8">
              Aquí os dejamos nuestro número de cuenta bancaria para cualquier detalle que deseéis hacernos.
            </p>
            <p className="text-lg md:text-xl text-white drop-shadow-md mb-8">
              Gracias por vuestra ayuda y por estar ahí para nosotros.
            </p>
            <div className="bg-white/90 backdrop-blur-sm p-6 rounded-lg shadow-lg inline-block">
              <p className="text-xl font-mono text-wedding-burgundy">
                ES66 0073 0100 5007 0230 4299
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* RSVP Section */}
      <Section 
        id="rsvp" 
        title="RSVP"
        backgroundImage={'/desktop-wallpaper-aesthetic-dark-green-leaves-aesthetic-leaf-thumbnail.jpg'}
        overlayStyle="darker"
        applyBlur={true}
      >
        <div className="max-w-2xl mx-auto relative z-20 p-4 md:p-8 bg-black/30 backdrop-blur-sm rounded-xl font-outfit">
          <RSVPForm />
        </div>
      </Section>

      <Footer />
    </div>
  );
};

export default Index;
